import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Badge, 
  Box,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  AdminPanelSettings as AdminIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useNavigate, Link } from 'react-router-dom';

// Store
import { useStore } from '../../store/useStore';

const Navbar = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const { 
    isAuthenticated, 
    user, 
    logout, 
    getCartItemCount, 
    mobileMenuOpen, 
    toggleMobileMenu, 
    closeMobileMenu 
  } = useStore();
  
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  
  const cartItemCount = getCartItemCount();

  // Forzar actualización del estado de autenticación
  useEffect(() => {
    // Esto fuerza el re-render cuando cambia la autenticación
    const checkAuth = () => {
      // El store ya maneja la persistencia, solo necesitamos forzar el re-render
      console.log('🔐 Navbar - Estado de autenticación:', { isAuthenticated, user });
    };
    checkAuth();
  }, [isAuthenticated, user]);

  const handleUserMenuOpen = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleUserMenuClose();
    navigate('/');
  };

  const navigationItems = [
    // Eliminado el enlace de "Inicio" - ya no es necesario ya que el logo lleva al inicio
  ];

  const userMenuItems = isAuthenticated ? [
    // Mostrar siempre la opción de administración para cualquier usuario autenticado
    { label: 'Administración', path: '/admin', icon: <AdminIcon /> },
    { label: 'Cerrar Sesión', action: handleLogout, icon: <LogoutIcon /> }
  ] : [
    { label: 'Iniciar Sesión', path: '/login', icon: <PersonIcon /> }
  ];

  return (
    <>
      <AppBar 
        position="sticky" 
        elevation={2}
        sx={{ backgroundColor: '#fff' }}
      >
        <Toolbar sx={{ 
          justifyContent: 'space-between', 
          minHeight: { xs: 48, md: 64 }, 
          py: { xs: 0, md: 1 },
          color: '#111' // Letras negras
        }}>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              <Box
                component="img"
                src={process.env.PUBLIC_URL + '/images/logo.png'}
                alt="Logo Vinogenes"
                sx={{
                  height: { xs: 48, md: 64 },
                  width: 'auto',
                  mr: 1.25,
                  objectFit: 'contain'
                }}
              />
              <Typography
                variant="h4"
                sx={{
                  fontFamily: '"Le Jour Serif", "Playfair Display", serif',
                  fontWeight: 400,
                  color: '#111',
                  textDecoration: 'none',
                  fontSize: { xs: '1.35rem', md: '2.1rem' },
                  letterSpacing: '0.06em',
                  transition: 'color 0.2s',
                  '&:hover': {
                    color: '#111'
                  },
                  ml: 0
                }}
              >
                VINOGENES
              </Typography>
            </Link>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {navigationItems.map((item) => (
                <Button
                  key={item.path}
                  component={Link}
                  to={item.path}
                  sx={{ 
                    fontWeight: 500,
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: 'rgba(232, 213, 196, 0.1)',
                      color: '#E8D5C4'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
            </Box>
          )}

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Cart */}
            <IconButton
              onClick={() => navigate('/cart')}
              sx={{ 
                color: '#111',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.04)',
                  color: '#111'
                }
              }}
            >
              <Badge 
                badgeContent={cartItemCount} 
                sx={{
                  '& .MuiBadge-badge': {
                    backgroundColor: '#DAA520',
                    color: '#FFFFFF'
                  }
                }}
                max={99}
              >
                <CartIcon />
              </Badge>
            </IconButton>

            {/* User Menu */}
            {!isMobile ? (
              <>
                <IconButton
                  onClick={handleUserMenuOpen}
                  sx={{ 
                    color: '#111',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.04)',
                      color: '#111'
                    }
                  }}
                >
                  <PersonIcon />
                </IconButton>
                <Menu
                  anchorEl={userMenuAnchor}
                  open={Boolean(userMenuAnchor)}
                  onClose={handleUserMenuClose}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  {userMenuItems.map((item, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        if (item.action) {
                          item.action();
                        } else {
                          navigate(item.path);
                          handleUserMenuClose();
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.icon}
                        {item.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Menu>
              </>
            ) : (
              <IconButton
                onClick={toggleMobileMenu}
                sx={{ 
                  color: '#111',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    color: '#111'
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={closeMobileMenu}
      >
        <Box sx={{ width: 280, pt: 2 }}>
          {/* User Info */}
          <Box sx={{ px: 3, pb: 2 }}>
            {isAuthenticated ? (
              <Typography variant="h6" color="primary">
                Hola, {user?.name}
              </Typography>
            ) : (
              <Typography variant="h6" color="text.secondary">
                Menú de Usuario
              </Typography>
            )}
          </Box>
          
          <Divider />

          {/* Navigation Items */}
          <List>
            {navigationItems.map((item) => (
              <ListItem
                key={item.path}
                component={Link}
                to={item.path}
                onClick={closeMobileMenu}
                sx={{ color: 'inherit', textDecoration: 'none' }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>

          <Divider />

          {/* User Menu Items */}
          <List>
            {userMenuItems.map((item, index) => (
              <ListItem
                key={index}
                onClick={() => {
                  if (item.action) {
                    item.action();
                  } else {
                    navigate(item.path);
                  }
                  closeMobileMenu();
                }}
                sx={{ cursor: 'pointer' }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  );
};

export default Navbar; 